import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Collapse, Pagination, Alert } from 'react-bootstrap';
import campaignService from '../../services/campaign.service';
import Loading from '../common/Loading';
import {
    CampaignQueueStatusBadge,
    CampaignStatusBadge,
} from './campaignDetailUtils';
import { useCampaign } from './useCampaign';
import { useEyeconxStore } from '../../store/eyeconxStore';
import { getShortId, toProperCase } from '../process/processQueueUtils';
function CampaignQueue() {
    const { campaignId } = useParams();
    const user = useEyeconxStore((state) => state.user);
    const isAdmin = user?.role === 'ADMIN';
    const { campaign, isLoading: campaignLoading, error: campaignError, refetch } = useCampaign(campaignId);

    const [itemsPerPage, setItemsPerPage] = useState(30);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [queueItems, setQueueItems] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const isSms = campaign?.type === 'SMS';
    const queueLabel = isSms ? 'SMS Queue' : 'Email Queue';

    const fetchQueue = useCallback(() => {
        if (!campaignId || !campaign?.type || !isAdmin) return;

        setIsLoading(true);
        setError(null);

        const request = isSms
            ? campaignService.getSmsCampaignQueue(campaignId, currentPage, itemsPerPage)
            : campaignService.getEmailCampaignQueue(campaignId, currentPage, itemsPerPage);

        request
            .then((res) => {
                setQueueItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                setExpandedRowId(null);
            })
            .catch((err) => {
                setQueueItems([]);
                if (err?.response?.status === 403) {
                    setError('You do not have permission to view the campaign queue.');
                } else {
                    setError('Failed to load campaign queue. Please try again.');
                }
            })
            .finally(() => setIsLoading(false));
    }, [campaignId, campaign?.type, currentPage, itemsPerPage, isAdmin, isSms]);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    const pageStatusCounts = useMemo(() => {
        return queueItems.reduce(
            (acc, item) => {
                if (item.status === 'WAITING') acc.waiting += 1;
                else if (item.status === 'SENT') acc.sent += 1;
                else if (item.status === 'FAILED') acc.failed += 1;
                return acc;
            },
            { waiting: 0, sent: 0, failed: 0 }
        );
    }, [queueItems]);

    const handleRefresh = () => {
        refetch();
        fetchQueue();
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = queueItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    if (!isAdmin) {
        return (
            <div className="container-fluid px-3 px-lg-4">
                <div className="pagetitle">
                    <h1>
                        <i className="bi bi-list-ol"></i> Campaign Queue
                    </h1>
                </div>
                <Alert variant="warning">
                    Campaign queue is available to admin users only.{' '}
                    <Link to="/campaign">Return to campaigns</Link>
                </Alert>
            </div>
        );
    }

    return (
        <div className="container-fluid px-3 px-lg-4">
            <div className="pagetitle d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h1 className="mb-0">
                    <i className={`bi ${isSms ? 'bi-chat-dots' : 'bi-envelope'} me-2`}></i>
                    {queueLabel}
                </h1>
                <div className="d-flex align-items-center gap-2">
                    <Link to="/campaign" className="campaign-queue-page__back d-inline-flex align-items-center gap-1">
                        <i className="bi bi-arrow-left"></i>
                        Back to campaigns
                    </Link>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={campaignLoading || isLoading}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </Button>
                </div>
            </div>

            {campaignLoading ? (
                <Loading />
            ) : campaignError ? (
                <Alert variant="warning">{campaignError}</Alert>
            ) : (
                campaign && (
                    <>
                        <div className="campaign-queue-context p-3 p-md-4 mb-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                                <div className="flex-grow-1">
                                    <div className="text-muted small mb-1">{campaign.clinicName || '—'}</div>
                                    <div className="campaign-queue-context__title fw-semibold mb-1">
                                        {campaign.name || 'Untitled campaign'}
                                    </div>
                                    {campaign.title && (
                                        <div className="campaign-queue-context__meta text-muted mb-2">
                                            {campaign.title}
                                        </div>
                                    )}
                                    <div className="d-flex flex-wrap gap-2">
                                        <span
                                            className={`badge campaign-queue-type-badge ${
                                                isSms
                                                    ? 'bg-success bg-opacity-10 text-success'
                                                    : 'bg-primary bg-opacity-10 text-primary'
                                            }`}
                                        >
                                            <i
                                                className={`bi ${isSms ? 'bi-chat-dots' : 'bi-envelope'} me-1`}
                                            ></i>
                                            {campaign.type}
                                        </span>
                                        <span className="badge bg-light text-dark border campaign-queue-type-badge">
                                            {toProperCase(campaign.category)}
                                        </span>
                                        <CampaignStatusBadge
                                            status={campaign.status}
                                            statusCode={campaign.statusCode}
                                        />
                                    </div>
                                </div>
                                <div className="text-md-end">
                                    <div className="text-muted small mb-1">Campaign ID</div>
                                    <div className="font-monospace small text-break">{campaign.id}</div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-6 col-lg-3">
                                <div className="campaign-queue-stat campaign-queue-stat--total">
                                    <div className="campaign-queue-stat__label">Total in queue</div>
                                    <div className="campaign-queue-stat__value">{totalElements}</div>
                                </div>
                            </div>
                            <div className="col-6 col-lg-3">
                                <div className="campaign-queue-stat campaign-queue-stat--waiting">
                                    <div className="campaign-queue-stat__label">Waiting (this page)</div>
                                    <div className="campaign-queue-stat__value">{pageStatusCounts.waiting}</div>
                                </div>
                            </div>
                            <div className="col-6 col-lg-3">
                                <div className="campaign-queue-stat campaign-queue-stat--sent">
                                    <div className="campaign-queue-stat__label">Sent (this page)</div>
                                    <div className="campaign-queue-stat__value">{pageStatusCounts.sent}</div>
                                </div>
                            </div>
                            <div className="col-6 col-lg-3">
                                <div className="campaign-queue-stat campaign-queue-stat--failed">
                                    <div className="campaign-queue-stat__label">Failed (this page)</div>
                                    <div className="campaign-queue-stat__value">{pageStatusCounts.failed}</div>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm border-0 campaign-queue-table-card">
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center flex-wrap gap-1 mb-3">
                                    <i className="bi bi-table text-primary me-1"></i>
                                    <h5 className="card-title mb-0" style={{ color: '#012970' }}>
                                        Queue Items
                                        <span
                                            className="text-muted fw-normal"
                                            style={{ fontSize: '0.9rem', marginLeft: '0.35rem' }}
                                        >
                                            ({totalElements} total)
                                        </span>
                                    </h5>
                                </div>

                                {isLoading ? (
                                    <Loading />
                                ) : error && queueItems.length === 0 ? (
                                    <Alert variant="warning">{error}</Alert>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Patient</th>
                                                    <th>{isSms ? 'Phone' : 'Email'}</th>
                                                    <th>Sender</th>
                                                    <th>Status</th>
                                                    <th>Created</th>
                                                    <th style={{ width: '56px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {queueItems.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} className="text-center text-muted py-5">
                                                            <i className="bi bi-inbox d-block fs-3 mb-2"></i>
                                                            No queue items found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    queueItems.map((item) => (
                                                        <React.Fragment key={item.id}>
                                                            <tr
                                                                className={
                                                                    expandedRowId === item.id ? 'table-active' : ''
                                                                }
                                                            >
                                                                <td>
                                                                    <div className="fw-medium">
                                                                        {item.patientName || '—'}
                                                                    </div>
                                                                    <small
                                                                        className="font-monospace text-muted"
                                                                        title={item.patientId}
                                                                    >
                                                                        {getShortId(item.patientId)}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <small className="campaign-queue-receiver">
                                                                        {item.receiver || '—'}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <small>{item.sender || '—'}</small>
                                                                </td>
                                                                <td>
                                                                    <CampaignQueueStatusBadge status={item.status} />
                                                                </td>
                                                                <td>
                                                                    <small>{item.createdAt || '—'}</small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant={
                                                                            expandedRowId === item.id
                                                                                ? 'outline-secondary'
                                                                                : 'outline-primary'
                                                                        }
                                                                        size="sm"
                                                                        onClick={() => toggleRow(item.id)}
                                                                        title={
                                                                            expandedRowId === item.id
                                                                                ? 'Collapse'
                                                                                : 'Expand'
                                                                        }
                                                                        aria-expanded={expandedRowId === item.id}
                                                                    >
                                                                        <i
                                                                            className={`bi bi-chevron-${
                                                                                expandedRowId === item.id
                                                                                    ? 'up'
                                                                                    : 'down'
                                                                            }`}
                                                                        ></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                            <tr className="p-0 border-top-0">
                                                                <td colSpan={6} className="p-0 border-top-0">
                                                                    <Collapse in={expandedRowId === item.id}>
                                                                        <div className="p-3 bg-light border-top">
                                                                            <div className="row">
                                                                                <DetailField label="Queue ID" mono>
                                                                                    {item.id}
                                                                                </DetailField>
                                                                                <DetailField label="Campaign ID" mono>
                                                                                    {item.campaignId}
                                                                                </DetailField>
                                                                                <DetailField label="Clinic ID" mono>
                                                                                    {item.clinicId}
                                                                                </DetailField>
                                                                                <DetailField label="Patient ID" mono>
                                                                                    {item.patientId}
                                                                                </DetailField>
                                                                                <DetailField label="Status">
                                                                                    <CampaignQueueStatusBadge
                                                                                        status={item.status}
                                                                                    />
                                                                                </DetailField>
                                                                                <DetailField label="Status Code">
                                                                                    {item.statusCode}
                                                                                </DetailField>
                                                                                <DetailField label="Created At">
                                                                                    {item.createdAt}
                                                                                </DetailField>
                                                                                <DetailField label="Modified At">
                                                                                    {item.modifiedAt}
                                                                                </DetailField>
                                                                            </div>
                                                                        </div>
                                                                    </Collapse>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                    <div className="d-flex flex-wrap align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="small text-muted">Show</span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: '80px' }}
                                                value={itemsPerPage}
                                                onChange={handleItemsPerPageChange}
                                            >
                                                {[10, 30, 50, 100].map((n) => (
                                                    <option key={n} value={n}>
                                                        {n}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="small text-muted">entries</span>
                                        </div>
                                        <span className="text-muted small">
                                            Showing {startItem} to {endItem} of {totalElements}
                                        </span>
                                    </div>
                                    {paginationControls}
                                </div>
                            </div>
                        </div>
                    </>
                )
            )}
        </div>
    );
}

export default CampaignQueue;
